import { createContext, useCallback, useContext, useRef } from 'react'

import { identity } from 'es-toolkit'

import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'
import { StateCreator, createStore, useStore } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { useShallow } from 'zustand/react/shallow'

interface PageDimensions {
  widthInches: number
  heightInches: number
}

export interface PageDimensionsStoreState {
  pageDimensions: Record<number, PageDimensions | null>
  setPageDimensions: (
    pageNumber: number,
    pageDimensions: PageDimensions | null,
  ) => void
  resetPageDimensions: () => void

  pageHeights: Record<number, number>
  setPageHeight: (pageNumber: number, pageHeight: number) => void
  resetPageHeights: () => void
}

const INIT_STORE: StateCreator<PageDimensionsStoreState> = (set, get) => ({
  pageDimensions: {},
  setPageDimensions: (
    pageNumber: number,
    pageDimensions: PageDimensions | null,
  ) => {
    set((state) => {
      state.pageDimensions[pageNumber] = pageDimensions
      return state
    })
  },
  resetPageDimensions: () => {
    set((state) => {
      state.pageDimensions = {}
      return state
    })
  },

  pageHeights: {},
  setPageHeight: (pageNumber: number, pageHeight: number) => {
    set((state) => {
      state.pageHeights[pageNumber] = pageHeight
      return state
    })
  },
  resetPageHeights: () => {
    set((state) => {
      state.pageHeights = {}
      return state
    })
  },
})

export type PageDimensionsStore = ReturnType<typeof createPageDimensionsStore>

export const createPageDimensionsStore = () => {
  return createStore<PageDimensionsStoreState, [['zustand/immer', never]]>(
    immer<PageDimensionsStoreState>((set, get, store) => ({
      ...INIT_STORE(set, get, store),
    })),
  )
}

export const PdfViewerContext = createContext<PageDimensionsStore | null>(null)

export const PdfViewerProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const pageDimensionsStore = useRef<PageDimensionsStore | null>(null)

  if (!pageDimensionsStore.current) {
    pageDimensionsStore.current = createPageDimensionsStore()
  }

  return (
    <PdfViewerContext.Provider value={pageDimensionsStore.current}>
      {children}
    </PdfViewerContext.Provider>
  )
}

export const usePageDimensionsStore = <T,>(
  selector: (state: PageDimensionsStoreState) => T = identity as (
    state: PageDimensionsStoreState,
  ) => T,
) => {
  const context = useContext(PdfViewerContext)
  if (!context) {
    throw new Error(
      'usePageDimensionsStore must be used within a PdfViewerProvider',
    )
  }
  return useStore(context, selector)
}

// region: General

export const useResetStore = () => {
  const resetPageDimensions = useResetPageDimensions()
  const resetPageHeights = useResetPageHeights()
  return useCallback(() => {
    resetPageDimensions()
    resetPageHeights()
  }, [resetPageDimensions, resetPageHeights])
}

// endregion: General

// region: Page Dimensions

export const usePageDimensionsMap = () => {
  return usePageDimensionsStore(useShallow((state) => state.pageDimensions))
}

export const usePageDimensions = (pageNumber: number) => {
  return usePageDimensionsStore(
    useShallow((state) => state.pageDimensions[pageNumber] ?? null),
  )
}

export const useResetPageDimensions = () => {
  return usePageDimensionsStore((state) => state.resetPageDimensions)
}

export const useSetPageDimensions = (pageNumber: number) => {
  const setPageDimensions = usePageDimensionsStore(
    (state) => state.setPageDimensions,
  )
  return useCallback(
    (pageDimensions: PageDimensions | null) => {
      setPageDimensions(pageNumber, pageDimensions)
    },
    [setPageDimensions, pageNumber],
  )
}

// endregion: Page Dimensions

// region: Page Heights

export const usePageHeight = (pageNumber: number) => {
  return usePageDimensionsStore(
    useShallow((state) => state.pageHeights[pageNumber] ?? null),
  )
}

export const useResetPageHeights = () => {
  return usePageDimensionsStore((state) => state.resetPageHeights)
}

export const useSetPageHeight = (pageNumber: number) => {
  const setPageHeight = usePageDimensionsStore((state) => state.setPageHeight)
  return useCallback(
    (pageHeight: number) => {
      setPageHeight(pageNumber, pageHeight)
    },
    [setPageHeight, pageNumber],
  )
}

// endregion: Page Heights
